import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatSortModule, Sort } from '@angular/material/sort';
import { faAddressBook, faEnvelope, faEnvelopeOpen, faEraser, faExpand, faReply, faTrash } from '@fortawesome/free-solid-svg-icons';
import { DocumentPropertyCodes } from '@hub4b/constantes/document-property-codes';
import { HistoryPropertyCodes } from '@hub4b/constantes/history-property-codes';
import { SortDirections } from '@hub4b/constantes/sort-directions';
import { DetailTablePanelContextMenuItem } from '@hub4b/controls/section-table/section-table-context-menu/models/detail-table-panel-context-menu-item';
import { DetailTableHeaderCellComponent } from '@hub4b/controls/section-table/section-table-header-cell/detail-table-header-cell.component';
import { BaseDirective } from '@hub4b/directives/base.directive';
import { ComparaisonHelper } from '@hub4b/helpers/comparaison-helper';
import { SeverityLevel } from '@hub4b/models/enums/severity-level';
import { IFilterable } from '@hub4b/models/interfaces/filterable.interface';
import { ColumnDefinition } from '@hub4b/models/server/column-definition';
import { NgxSpinnerModule } from 'ngx-spinner';
import { finalize, takeUntil } from 'rxjs';
import { TableFilterHandler } from 'src/app/core/table-filter-handler';
import { DetailTableDataType } from 'src/app/models/enums/detail-data-type';
import { DocumentFlux } from 'src/app/models/enums/document-flux';
import { PropertyType } from 'src/app/models/enums/property-type';
import { Folder } from 'src/app/models/server/folder';
import { History } from 'src/app/models/server/history';
import { SpinnerService } from 'src/app/services/core/spinner.service';
import { HistorySectionEventsService } from 'src/app/services/events/history-section-events.service';
import { DetailBaseDatasource } from '../../../datasources/detail-base-datasource';
import { HistoryDatasource } from './data-sources/history-datasource';
import { HistoryTableCellComponent } from './history-table-cell/history-table-cell.component';

@Component({
    selector: 'app-history-section',
    templateUrl: './history-section.component.html',
    styleUrls: ['./history-section.component.scss'],
    providers: [
        {
            provide: HistoryDatasource,
            useClass: HistoryDatasource,
            deps: [DetailBaseDatasource],
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatSortModule, DetailTableHeaderCellComponent, NgClass, HistoryTableCellComponent, NgxSpinnerModule]
})
export class HistorySectionComponent extends BaseDirective implements IFilterable, OnInit, OnDestroy {
    /**
     * Références des icônes utilisés dans le template.
     */
    public resetFilterIcon = faEraser;
    public faExpand = faExpand;
    public readIcon = faEnvelopeOpen;
    public unreadIcon = faEnvelope;
    public addressBookIcon = faAddressBook;
    public trashIcon = faTrash;
    public replyIcon = faReply;

    /**
     * Références des Enums utilisées dans le template.
     */
    public PropertyType = PropertyType;
    public DocumentFlux = DocumentFlux;
    public DetailTableDataType = DetailTableDataType;
    public SeverityLevel = SeverityLevel;

    /**
     * Instance du dossier pour lequel l'historique est affiché.
     */
    @Input()
    public folder: Folder;

    /**
     * Ligne de l'historique sélectionnée.
     */
    @Input()
    public selectedHistory: string = null;

    /**
     * Emis lorsque l'utilisateur sélectionne une ligne d'historique.
     */
    @Output()
    public historySelected: EventEmitter<History> = new EventEmitter();

    /**
     * Données brutes du tableau de documents (avant filtrage)
     */
    public rawDocuments: History[];

    /**
     * Données filtrées du tableau de documents .
     */
    public filteredDocuments: History[];

    /**
     * Tri courant.
     */
    public currentSort: Sort = this.datasource.defaultSort;

    /**
     * Définitions des colonnes du tableau.
     */
    public displayedColumnsDefinitions: ColumnDefinition[];

    /**
     * Gestionnaire des filtres de l'en-tête du tableau.
     */
    public tableHeaderFilterHandler: TableFilterHandler;

    /**
     * Gestionnaire des filtres de l'en-tête de la section.
     */
    public panelHeaderFilterHandler: TableFilterHandler;

    /**
     * Liste des éléments du menu contextuel.
     */
    public contextMenuItems: Array<DetailTablePanelContextMenuItem<History>> = [];

    /**
     * Position sur l'axe X de la souris au moment d'un clic droit (utilisé pour positionner le menu contextuel)
     */
    protected rightClickMenuPositionX: number;

    /**
     * Position sur l'axe Y de la souris au moment d'un clic droit (utilisé pour positionner le menu contextuel)
     */
    protected rightClickMenuPositionY: number;

    /**
     * Constructeur.
     * @param datasource
     * @param historySectionEventsService
     * @param spinner
     * @param host
     */
    constructor(
        public datasource: HistoryDatasource,
        private historySectionEventsService: HistorySectionEventsService,
        protected spinner: SpinnerService,
        private cdr: ChangeDetectorRef
    ) {
        super();
        this.tableHeaderFilterHandler = new TableFilterHandler(this);
        this.panelHeaderFilterHandler = new TableFilterHandler(this);
    }

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.displayedColumnsDefinitions = this.datasource.getColumnDefinitions();

        this.tableHeaderFilterHandler.initializeFilters(this.displayedColumnsDefinitions);
        this.panelHeaderFilterHandler.initializeFilters([
            {
                propertyCode: 'search',
                header: 'search',
                type: PropertyType.Text,
                displayFormat: null,
                size: 0,
                values: null,
                custom: false,
                isStickyColumn: false,
            },
        ]);

        this.loadHistories();

        this.subscribeToEvents();
    }

    /**
     * Appelée à la destruction du composant.
     */
    override ngOnDestroy(): void {
        super.ngOnDestroy();

        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();

        if (this.tableHeaderFilterHandler) {
            this.tableHeaderFilterHandler.dispose();
        }

        if (this.panelHeaderFilterHandler) {
            this.panelHeaderFilterHandler.dispose();
        }
    }

    /**
     * Appelée lorsque l'utilisateur selectionne une ligne d'historique.
     * @param history Instance de la ligne d'historique selectionnée.
     */
    public onHistorySelected(history: History): void {
        this.selectHistory(history);
    }

    /**
     * Souscrit aux événements des autres composants.
     */
    private subscribeToEvents() {
        // Souscrit aux evenements de raffraichissement de la section 'Historique'.
        this.historySectionEventsService.needRefresh.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            if (!!event && event.folderId == this.folder.id) {
                //console.warn(`Réception de l'événement HistorySectionRefreshEvent par le composant HistoryComponent du dossier ${this.folder.id}`, event);
                this.spinner.show('history-spinner');
                this.processDatas(event.histories);
                this.cdr.markForCheck();
                this.spinner.hide('history-spinner');
            }
        });
    }

    /**
     * Charge les lignes d'historique du dossier.
     */
    private loadHistories(): void {
        this.spinner.show('history-spinner');
        this.datasource
            .listDatas(this.folder.viewId, this.folder.id)
            .pipe(
                takeUntil(this.componentDestroyed$),
                finalize(() => {
                    this.spinner.hide('history-spinner');
                })
            )
            .subscribe((details) => {
                this.processDatas(details);
                this.cdr.markForCheck();
            });
    }

    /**
     * Traite les données à afficher dans le tableau.
     * @param histories Données à afficher dans le tableau.
     */
    private processDatas(histories: History[]): void {
        this.rawDocuments = histories;
        this.applyFilters();

        if (this.filteredDocuments.length > 0) {
            this.selectHistory(this.filteredDocuments[0].id);
        }
    }

    /**
     * Sélectionne une ligne d'historique.
     * @param dataId : Instance de la ligne d'historique.
     */
    private selectHistory(history: History) {
        if (!!history.id) {
            this.selectedHistory = history.id;
            let detail = this.rawDocuments.find((x) => x.id == history.id);
            this.historySelected.emit(detail);
        }
    }

    /**
     * Retourne le style du menu contextuel utilisé pour positionner le menu contextuel.
     * @returns : Le style du menu contextuel.
     */
    public getContextMenuStyle(): any {
        // Positionne le menu contextuel au niveau du pointeur de la souris.
        return {
            position: 'fixed',
            left: `${this.rightClickMenuPositionX}px`,
            top: `${this.rightClickMenuPositionY}px`,
        };
    }

    /**
     * Applique les filtres de recherche et/ou de colonnes ainsi que le tri sur la liste de dossiers.
     */
    public applyFilters(): void {
        if (!!this.rawDocuments) {
            const datas = this.rawDocuments.slice();
            this.filteredDocuments = datas.filter((x) => this.isMatchTableHeaderFilters(x) && this.isMatchPanelHeaderFilters(x));
            this.applySort();
        }
    }

    /**
     * Trie les données du tableau
     * @param sort : information sur le tri.
     */
    protected sort(sort: Sort): void {
        this.currentSort = sort;
        this.applyFilters();
    }

    /**
     * Applique le tri.
     */
    private applySort(): void {
        if (this.currentSort.direction) {
            this.filteredDocuments = this.filteredDocuments.sort((a, b) => {
                const isAsc = this.currentSort.direction === SortDirections.ASC;
                if (
                    this.currentSort.active == HistoryPropertyCodes.DATE ||
                    this.currentSort.active == DocumentPropertyCodes.CREATION_DATE ||
                    this.currentSort.active == DocumentPropertyCodes.ORIGINAL_DATE
                ) {
                    return ComparaisonHelper.compare(Date.parse(a[this.currentSort.active]), Date.parse(b[this.currentSort.active]), isAsc);
                } else {
                    return ComparaisonHelper.compare(a[this.currentSort.active], b[this.currentSort.active], isAsc);
                }
            });
        }
    }

    /**
     * Indique si une donnée respecte les filtres de l'en-tête de section.
     * @param data : donnée à tester.
     * @returns True Si la donnée respecte tous les filtres de l'en-tête de section; false autrement.
     */
    private isMatchPanelHeaderFilters(data: History): boolean {
        if (!!this.panelHeaderFilterHandler.filterValues['search']) {
            let result = false;

            // Vérifie si au moins l'une des valeurs de cellules (autre que les cellules de type date) contient la valeur recherchée.
            result = this.displayedColumnsDefinitions.some((column) => {
                if (column.type === PropertyType.Text) {
                    const value = data[column.propertyCode];
                    const searchedValue = this.panelHeaderFilterHandler.filterValues['search'].values[0].toLowerCase();

                    if (value && value.toLowerCase().includes(searchedValue)) {
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    return false;
                }
            });

            return result;
        } else {
            // Aucun paramètres de recherche --> Tous les dossiers sont retournés.
            return true;
        }
    }

    /**
     * Indique si une donnée respecte les filtres de l'en-tête du tableau.
     * @param data : donnée à tester.
     * @returns True Si la donnée respecte tous les filtres de l'en-tête du tableau; false autrement.
     */
    private isMatchTableHeaderFilters(data): boolean {
        let isMatch: boolean = true;

        this.displayedColumnsDefinitions.forEach((column) => {
            if (!!this.tableHeaderFilterHandler.filterValues[column.propertyCode]) {
                let value = data[column.propertyCode];

                if (
                    column.propertyCode == HistoryPropertyCodes.DATE ||
                    column.propertyCode == DocumentPropertyCodes.CREATION_DATE ||
                    column.propertyCode == DocumentPropertyCodes.ORIGINAL_DATE
                ) {
                    value = data.date.slice(0, 10);
                } else if (column.propertyCode == DocumentPropertyCodes.ATTACHMENTS) {
                    value = data.attachments.length > 0 ? 'true' : 'false';
                }

                let filterValue = this.tableHeaderFilterHandler.filterValues[column.propertyCode].values[0];

                if (typeof value == 'string') {
                    if (!value.toLowerCase().includes(filterValue.toLowerCase())) {
                        isMatch = false;
                    }
                } else {
                    if (!(value == filterValue)) {
                        isMatch = false;
                    }
                }
            }
        });

        return isMatch;
    }
}
